import { useState } from 'react';
import { Share2, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * Botón para que un admin publique un contenido (noticia o evento) en el feed de socios.
 * Props:
 *  - contentType: 'news' | 'event'
 *  - refId: slug o id del contenido
 *  - title: título a mostrar en el modal
 *  - testid: prefix para data-testid
 *  - size: 'sm' | 'md'
 */
export default function BroadcastToFeedButton({ contentType, refId, title, testid = 'broadcast-feed', size = 'md' }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setSending(true);
    try {
      await api.post('/admin/community/broadcast', {
        content_type: contentType,
        ref_id: refId,
        text: text.trim(),
      });
      setDone(true);
      toast.success('Publicado en el feed de socios');
      setTimeout(() => {
        setOpen(false);
        setDone(false);
        setText('');
      }, 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo publicar');
    } finally {
      setSending(false);
    }
  };

  const triggerCls =
    size === 'sm'
      ? 'inline-flex items-center gap-1.5 rounded-full border border-cnib-blue/20 bg-cnib-blue/5 px-3 py-1.5 text-xs font-bold text-cnib-blue transition-colors hover:bg-cnib-blue hover:text-white'
      : 'inline-flex items-center gap-2 rounded-full bg-cnib-blue px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-cnib-blue/90';

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={triggerCls} data-testid={`${testid}-open`}>
        <Share2 className="h-3.5 w-3.5" />
        Publicar en feed
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4" onClick={() => setOpen(false)}>
          <div
            className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testid}-modal`}
          >
            <div className="flex items-center justify-between border-b border-slate-100 p-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Compartir en feed</p>
                <h3 className="font-heading text-lg font-extrabold text-slate-900">{title}</h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full p-1 text-slate-400 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3 p-5">
              <p className="text-xs text-slate-500">
                Esta publicación aparecerá como una tarjeta en el feed de todos los socios activos. Deja en blanco para usar un texto automático.
              </p>
              <textarea
                rows={3}
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={1000}
                placeholder={contentType === 'news' ? 'Mensaje para los socios (opcional)…' : 'Invitación al evento (opcional)…'}
                className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none placeholder:text-slate-400 focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15"
                data-testid={`${testid}-text`}
              />
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 p-4">
              <button type="button" onClick={() => setOpen(false)} className="rounded-full px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-100">
                Cancelar
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={sending || done}
                className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-orange-600 disabled:opacity-60"
                data-testid={`${testid}-submit`}
              >
                {done ? <Check className="h-4 w-4" /> : sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                {done ? 'Publicado' : 'Publicar en feed'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
